import { useState, type ChangeEvent, type FormEvent } from "react";
import { topics } from "@/mocks/topics";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const benefits = [
  "每日精选热点摘要，一次看完不留死角",
  "重大话题的第一时间提醒与后续复盘",
  "可随时取消，不会频繁打扰",
];

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(e.target.value.length);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/dailcbbp14a1h3mr763g",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: payload.toString(),
        },
      );

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? String(serverMsg)
            : "提交失败，请稍后重试，或直接发邮件给我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <section id="subscribe" className="bg-background-50 py-14 md:py-20 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="rounded-lg bg-primary-900 text-background-50 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-widest uppercase text-primary-200">
              <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
              热点订阅
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold leading-snug">
              蹲不到前排，也别错过重点
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed">
              留下邮箱，我们把每天最值得关注的话题整理成一封简短的信。也欢迎顺手留下你想看的线索，编辑会认真看。
            </p>
            <ul className="mt-6 flex flex-col gap-3">
              {benefits.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-background-100">
                  <i className="ri-check-line w-5 h-5 flex items-center justify-center text-accent-300 shrink-0"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-background-50 p-5 md:p-6">
            <form
              id="subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div>
                <label
                  htmlFor="subscribe-email"
                  className="block text-sm font-label font-medium text-foreground-800"
                >
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="mt-2 w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                />
              </div>

              <div>
                <label
                  htmlFor="subscribe-topic"
                  className="block text-sm font-label font-medium text-foreground-800"
                >
                  最想关注的方向
                </label>
                <div className="relative mt-2">
                  <select
                    id="subscribe-topic"
                    name="topic"
                    defaultValue=""
                    className="w-full appearance-none px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 cursor-pointer"
                  >
                    <option value="">全都要，全部推给我</option>
                    {topics.map((topic) => (
                      <option key={topic.id} value={topic.name}>
                        {topic.name}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label
                  htmlFor="subscribe-message"
                  className="block text-sm font-label font-medium text-foreground-800"
                >
                  想看的线索或建议（选填）
                </label>
                <textarea
                  id="subscribe-message"
                  name="message"
                  maxLength={500}
                  rows={3}
                  onChange={handleMessageChange}
                  placeholder="例如：想了解某个话题的来龙去脉，或提供一条可查证的公开线索"
                  className="mt-2 w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400 font-label">
                  {messageLength} / 500
                </p>
              </div>

              <input
                type="text"
                name="website_alt"
                className="extra-line-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="flex items-start gap-2 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5">
                  <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
                  订阅成功，感谢加入。我们会把精选内容发到你的邮箱。
                </p>
              )}

              {status === "error" && (
                <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                {status === "loading" ? "提交中…" : "订阅热点"}
              </button>

              <p className="text-xs text-foreground-500 font-label leading-relaxed">
                提交即表示同意我们通过邮箱向你发送内容摘要。我们不会将你的信息用于其他用途。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}